import { useLocation } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useSession } from "@/lib/auth-client";
import { useGetMyInvitations } from "../api/hooks";

const SEEN_INVITATIONS_KEY_PREFIX = "seen-invitations";

const getSeenInvitationIds = (key: string): string[] => {
  try {
    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
    const parsed = JSON.parse(localStorage.getItem(key) ?? "[]") as unknown;
    return Array.isArray(parsed)
      ? parsed.filter((value): value is string => typeof value === "string")
      : [];
  } catch {
    return [];
  }
};

/**
 * Hook to automatically show the invitations modal when an account receives
 * invitations it has not seen before.
 *
 * Uses localStorage keyed by accountId (Ory identity UUID), storing the set of
 * invitation IDs that have already triggered an automatic open. This keeps the
 * modal from reopening on refresh for the same invitations while still
 * re-triggering when new invitations arrive later.
 */
export function useInvitationsModalTrigger() {
  const [shouldShow, setShouldShow] = useState(false);
  const { data } = useGetMyInvitations();
  const { data: session } = useSession();
  const location = useLocation();
  const accountId = session?.user?.accountId;

  // Check if user is already on the invitations page
  const isOnInvitationsPage = location.pathname.includes("/invitations");

  useEffect(() => {
    if (!accountId) {
      return;
    }

    const currentInvitationIds = data.items.map(
      (invitation) => invitation.invitation_id
    );

    if (currentInvitationIds.length === 0) {
      return;
    }

    const key = `${SEEN_INVITATIONS_KEY_PREFIX}-${accountId}`;
    const seenInvitationIds = new Set(getSeenInvitationIds(key));
    const hasNewInvitations = currentInvitationIds.some(
      (invitationId) => !seenInvitationIds.has(invitationId)
    );

    if (!hasNewInvitations) {
      return;
    }

    const updatedSeenInvitationIds = [
      ...new Set([...seenInvitationIds, ...currentInvitationIds]),
    ];
    localStorage.setItem(key, JSON.stringify(updatedSeenInvitationIds));

    if (!isOnInvitationsPage) {
      setShouldShow(true);
    }
  }, [accountId, data, isOnInvitationsPage]);

  return {
    shouldShow,
    onOpenChange: setShouldShow,
  };
}
